import { flash } from "@xola/ui-kit";
import axios from "axios";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { Virtuoso } from "react-virtuoso";
import { TelemetryModule } from "types";
import { api } from "../../api/api";
import { useInfiniteCollection } from "../../hooks/useInfiniteCollection";
import { useInvalidate } from "../../hooks/useInvalidation";
import { PromptModal } from "../Common/PromptModal";
import { ErrorBoundary } from "../ErrorBoundary";
import { Loader } from "../Loader";
import { TelemetryModuleItem } from "./TelemetryModuleItem";

interface ServerSideTrackingListProps {
    height: number;
}

export const ServerSideTrackingList = ({ height }: ServerSideTrackingListProps) => {
    const {
        data: telemetryModules,
        isLoading,
        fetchNextPage,
        isFetchingNextPage,
        hasNextPage,
    } = useInfiniteCollection<TelemetryModule>("telemetryModules");
    const invalidate = useInvalidate();
    const [isDelete, setDelete] = useState(false);
    const [moduleId, setModuleId] = useState<ID | null>(null);

    const { mutateAsync: deleteModule } = useMutation(async (moduleId: ID) => {
        return api.delete(`/telemetryModules/${moduleId}`);
    });

    const onDelete = async (moduleId: string) => {
        setModuleId(moduleId);
        toggleDeleteModal();
    };

    const toggleDeleteModal = () => {
        setDelete(!isDelete);
    };

    const handleDeleteConfirm = async () => {
        try {
            await deleteModule(moduleId!);
            invalidate("telemetryModules");
        } catch (error: any) {
            let { message }: { message: string } = error;
            if (error.response && axios.isAxiosError(error)) {
                const { data = {} }: any = error.response;
                message = data.field?.telemetryModule?.message ?? message;
            }

            flash.show({ text: message, color: "danger" });
        } finally {
            toggleDeleteModal();
        }
    };

    return (
        <div className="flex h-full lg:flex-row">
            {telemetryModules.length > 0 ? (
                <Virtuoso
                    style={{ height }}
                    className="w-full"
                    data={telemetryModules}
                    endReached={async () => {
                        if (hasNextPage) {
                            fetchNextPage();
                        }
                    }}
                    itemContent={(index, telemetryModule) => {
                        return (
                            <ErrorBoundary fallbackMessage={`Cannot render module ${telemetryModule.id}`}>
                                <TelemetryModuleItem
                                    key={telemetryModule.id}
                                    telemetryModule={telemetryModule}
                                    onDelete={onDelete}
                                />
                            </ErrorBoundary>
                        );
                    }}
                />
            ) : (
                <NoTelemetryModulesMessage isLoading={isLoading} />
            )}

            {isFetchingNextPage ? <Loader /> : null}

            <PromptModal
                title="Delete Module?"
                isOpen={isDelete}
                onConfirm={handleDeleteConfirm}
                onClose={toggleDeleteModal}
            >
                Are you sure you want to delete this module?
            </PromptModal>
        </div>
    );
};

const NoTelemetryModulesMessage = ({ isLoading }: { isLoading: boolean }) => {
    return (
        <div className="m-4 mx-2 p-2 text-gray-darker">
            {isLoading ? (
                <Loader size="tiny" className="!p-1" text="Loading Modules" />
            ) : (
                <span>No tracking modules found</span>
            )}
        </div>
    );
};
